import { useLocation, useNavigate } from 'react-router-dom';
import { useTripVisitStats } from '../../hooks/trip/tripVisitStats';
import { useTripData } from '../../hooks/trip/useTripData';
import { useTripInteractions } from '../../hooks/trip/useTripInteractions';
import { useTripOwnerProfile } from '../../hooks/trip/useTripOwnerProfile';
import { useScrollToAnchor } from '../../hooks/trip/useScrollToAnchor';
import { useAuthenticatedUser } from '../../hooks/useAuthenticatedUser';
import NotFoundPage from '../notFound/notFoundPage';
import { TripHeader } from './TripHeader';
import { TripActions } from './TripActions';
import { StoryList } from './StoryList';

/**
 * Trip page component - refactored to separate business logic from UI.
 * All business logic is now in custom hooks, making it reusable for React Native.
 */
export const TripPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Authentication state
  const { profile: user } = useAuthenticatedUser();

  // Business logic hooks (all platform-agnostic)
  const { data, loading, error, linkSecret } = useTripData(location.pathname);
  const { interactions } = useTripInteractions(data?.trip?.id, linkSecret, user?.userId);
  const { ownerProfile, isOwner } = useTripOwnerProfile(data?.trip?.ownerId, user?.userId);

  // Track visit stats
  useTripVisitStats(data, linkSecret);

  // Scroll to anchor functionality (web-specific)
  useScrollToAnchor(data?.stories);

  // Event handlers
  const handleEditClick = (tripId: string) => navigate(`/trips/${tripId}`);
  const handleAuthorClick = (userId: string) => navigate(`/profile/${userId}`);

  // Rendering
  if (error) {
    return <NotFoundPage />;
  }

  if (loading || !data) {
    return <div className="min-h-screen bg-canvas" />;
  }

  return (
    <div className="bg-canvas min-h-screen pb-20">
      <TripHeader trip={data.trip} ownerProfile={ownerProfile} onAuthorClick={handleAuthorClick} />

      <TripActions
        isOwner={isOwner}
        tripId={data.trip.id}
        linkSecret={linkSecret}
        onEditClick={handleEditClick}
      />

      <StoryList
        stories={data.stories}
        linkSecret={linkSecret}
        interactions={interactions}
        isOwner={isOwner}
      />
    </div>
  );
};
